/**
 * ============================================================================
 * ESCROW TIMELINE COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Visual timeline of escrow lifecycle events
 */

import React from 'react';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import StatusBadge from '../common/StatusBadge';

/**
 * Escrow Timeline Component
 * @param {Object} props
 * @param {Object} props.escrow - Escrow object
 * @param {Array} props.history - Escrow history events
 * @returns {JSX.Element}
 */
const EscrowTimeline = ({ escrow, history = [] }) => {
  const { formatDate, formatTime, language } = useI18n();

  if (!escrow) return null;

  const getEventIcon = (eventType) => {
    const icons = {
      created: '📦',
      funded: '💰',
      frozen: '❄️',
      unfrozen: '🔓',
      released: '✅',
      refunded: '↩️',
      partial_release: '📊',
      auto_release: '⏰',
    };
    return icons[eventType] || '📋';
  };

  const getEventColor = (eventType) => {
    const colors = {
      created: 'from-gray-500 to-slate-500',
      funded: 'from-blue-500 to-cyan-500',
      frozen: 'from-red-500 to-pink-500',
      unfrozen: 'from-green-500 to-emerald-500',
      released: 'from-green-500 to-teal-500',
      refunded: 'from-purple-500 to-pink-500',
      partial_release: 'from-indigo-500 to-purple-500',
      auto_release: 'from-yellow-500 to-orange-500',
    };
    return colors[eventType] || 'from-gray-500 to-slate-500';
  };

  const getEventLabel = (eventType) => {
    const labels = {
      created: language === 'fr' ? 'Escrow créé' : 'Escrow Created',
      funded: language === 'fr' ? 'Fonds reçus' : 'Funds Received',
      frozen: language === 'fr' ? 'Escrow gelé' : 'Escrow Frozen',
      unfrozen: language === 'fr' ? 'Escrow dégelé' : 'Escrow Unfrozen',
      released: language === 'fr' ? 'Fonds libérés' : 'Funds Released',
      refunded: language === 'fr' ? 'Fonds remboursés' : 'Funds Refunded',
      partial_release: language === 'fr' ? 'Libération partielle' : 'Partial Release',
      auto_release: language === 'fr' ? 'Libération automatique' : 'Auto Release',
    };
    return labels[eventType] || eventType;
  };

  // Build timeline events from escrow data and history
  const timelineEvents = [];

  if (escrow.createdAt) {
    timelineEvents.push({
      type: 'created',
      date: escrow.createdAt,
      description: language === 'fr' ? 'Compte escrow créé' : 'Escrow account created',
    });
  }

  if (escrow.fundedAt) {
    timelineEvents.push({
      type: 'funded',
      date: escrow.fundedAt,
      description: language === 'fr' ? 'Fonds verrouillés en escrow' : 'Funds locked in escrow',
      amount: escrow.amount,
    });
  }

  if (escrow.frozenAt) {
    timelineEvents.push({
      type: 'frozen',
      date: escrow.frozenAt,
      description: language === 'fr' ? 'Escrow gelé en raison d\'un litige' : 'Escrow frozen due to dispute',
    });
  }

  if (escrow.releasedAt) {
    timelineEvents.push({
      type: escrow.autoReleaseAt && new Date(escrow.releasedAt) >= new Date(escrow.autoReleaseAt) ? 'auto_release' : 'released',
      date: escrow.releasedAt,
      description: language === 'fr' ? 'Fonds libérés au vendeur' : 'Funds released to seller',
      amount: escrow.releaseAmount || escrow.amount,
    });
  }

  // Add history events
  history.forEach((event) => {
    timelineEvents.push({
      type: event.type || event.event_type,
      date: event.createdAt || event.timestamp,
      description: event.description || event.message,
      amount: event.amount,
    });
  });

  // Sort by date (newest first)
  timelineEvents.sort((a, b) => new Date(b.date) - new Date(a.date));

  if (timelineEvents.length === 0) {
    return (
      <Card variant="glass" className="p-12 text-center">
        <p className="text-gray-500 dark:text-gray-400">
          {language === 'fr' ? 'Aucun événement' : 'No events yet'}
        </p>
      </Card>
    );
  }

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <h3 className="text-xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-2">
        <span className="text-2xl">📅</span>
        {language === 'fr' ? 'Chronologie de l\'escrow' : 'Escrow Timeline'}
      </h3>
      <div className="space-y-4">
        {timelineEvents.map((event, index) => (
          <div
            key={index}
            className="flex items-start gap-4 animate-fadeInUp"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            {/* Timeline Line */}
            {index < timelineEvents.length - 1 && (
              <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-green-500"></div>
            )}

            {/* Icon */}
            <div
              className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getEventColor(
                event.type
              )} flex items-center justify-center text-xl shadow-lg flex-shrink-0 relative z-10`}
            >
              {getEventIcon(event.type)}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="font-black text-gray-900 dark:text-white">
                  {getEventLabel(event.type)}
                </h4>
                {event.amount && (
                  <span className="text-sm font-bold text-gray-600 dark:text-gray-400">
                    {formatCurrency(event.amount, escrow.currency || 'XAF')}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                {event.description}
              </p>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span>{formatDate(event.date)}</span>
                <span>•</span>
                <span>{formatTime(event.date)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default EscrowTimeline;
